<template>
  <div class="product-section" v-if="section" ref="sectionRef">

    <div class="c-section-top"/>

    <q-no-ssr>
      <SectionFiltersDialog
          v-if="filtersDialog"
          v-model="filtersDialog"
          :section="section"
          :value-primary-tag="selectedTagPrimary"
          :value-secondary-tag="selectedTagsSecondary"
          :primary-tags="tagsPrimary"
          :secondary-tags="tagsSecondary"
          :apply="onFiltersDialogApply"
      />
    </q-no-ssr>

    <div
        class="c-header-desktop bg-white swipe-disable gt-md"
        :class="{
          '--stickable': stickable
        }"
        :style="{
          top: cachedHeaderHeightCss
        }"
        v-sticky-state="{
          activeClass: 'stacked',
          top: cachedHeaderHeightCss,
          disable: !stickable
        }"
    >
      <div class="row items-center q-col-gutter-md no-wrap">
        <div class="col-shrink">
          <div class="q-px-sm q-px-lg-none">
            <slot name="title">
              <h2 class="q-ma-none text-weight-boldest s-font-lg s-font-md-4xl s-font-lg-2xl s-font-xl-2xl">
                {{ section.NAME }}
              </h2>
            </slot>
          </div>
        </div>
        <div class="col-shrink">
          <q-no-ssr>
            <TagsTabs
                v-model="selectedTagPrimary"
                :options="tagsPrimary"
                v-bind="defaultEntityOptionsProps"
                option-featured="FEATURED"
                class="c-tags-primary"
                v-if="$q.screen.gt.sm"
            />
          </q-no-ssr>
        </div>
      </div>
    </div>
    <q-no-ssr>
      <div class="row items-center q-col-gutter-md no-wrap" v-if="$q.screen.gt.md">
        <div class="col-shrink">
          <TagsChips
              :items="tagsSecondary"
              v-model="selectedTagsSecondary"
              title="Состав"
              class="c-tags-secondary q-pt-sm"
          />
        </div>
      </div>
    </q-no-ssr>

      <div
          class="c-header-mobile bg-white q-px-md swipe-disable lt-lg swipe-hor"
          :class="{
            '--with-tags': tagsPrimary.length > 0
          }"
      >
        <div class="row items-center q-col-gutter-xs">
          <div class="col-lg-grow">
            <h2
                v-if="!$slots.title"
                class="q-ma-none text-weight-boldest s-font-lg s-font-md-4xl s-font-lg-2xl s-font-xl-2xl"
            >
              {{ section.NAME }}
            </h2>
            <div
                v-else
                class="q-ma-none text-weight-boldest s-font-lg s-font-md-4xl s-font-lg-2xl s-font-xl-2xl"
            >
              {{ section.NAME }}
            </div>
          </div>
          <div class="col-lg-shrink q-ml-auto">
            <q-btn
                dense
                flat
                size="md"
                color="primary"
                :icon="viewMode === 'grid' ? ICONS.list :  ICONS.grid"
                style="margin-right: -6px;"
            >
              <ViewModeMenu
                  :view-mode="viewMode"
                  :sort-option="sortOption"
                  :sort-options="sortOptions"
                  @update:view-mode="onViewModeChange"
                  @update:sort-option="onSortChange"
              />
            </q-btn>
          </div>
        </div>
      </div>

      <div
          class="c-tags-primary-mobile bg-white q-pb-sm swipe-disable swipe-hor lt-lg"
          :style="{
            top: (cachedHeaderHeight - 1) + 'px'
          }"
          v-if="tagsPrimary.length > 1"
      >
        <div class="row items-center no-wrap">
          <div class="col q-pl-sm">
            <TagsTabs
                v-model="selectedTagPrimary"
                :options="tagsPrimary"
                v-bind="defaultEntityOptionsProps"
                all-tab-mode="inline"
                class="c-tags-primary"
            />
          </div>
          <div class="col-shrink c-filter-button q-pr-sm" v-if="hasFilters">
            <q-btn
                :icon="ICONS.filters"
                dense
                flat
                size="md"
                color="primary"
                @click="onFiltersOpen"
            />
          </div>
        </div>
      </div>
      <div class="c-tags-secondary-mobile q-pb-sm q-pl-sm swipe-hor lt-md">
        <TagsChips
            :items="tagsSecondary"
            v-model="selectedTagsSecondary"
            title="Состав"
            class="c-tags-secondary q-pt-sm q-mx-sm"
            ref="refTagsSecondary"
        />
      </div>

    <div class="c-products q-mt-md q-mx-md q-mx-lg-none" :key="viewMode">

      <div v-if="isFiltering" class="c-products__loading">
        <q-linear-progress indeterminate color="primary" size="2px"/>
      </div>

      <TransitionGroup
          v-if="shouldUseTransition"
          name="list"
          tag="div"
          class="c-products__list row q-col-gutter-md"
          :class="{ '--filtering': isFiltering }"
      >
        <component
            v-for="product in productsFiltered"
            :key="product.ID"
            :product="product"
            :class="viewVariant.cellClass"
            :is="viewVariant.cellComponent"
            :viewMode="viewMode"
            :orientation="viewOrientation(product)"
        />
      </TransitionGroup>

      <div
          v-else
          class="c-products__list row q-col-gutter-md"
          :class="{ '--filtering': isFiltering }"
      >
        <component
            v-for="product in productsFiltered"
            :key="product.ID"
            :product="product"
            :class="viewVariant.cellClass"
            :is="viewVariant.cellComponent"
            :viewMode="viewMode"
            :orientation="viewOrientation(product)"
        />
      </div>
    </div>

  </div>

</template>

<script setup lang="ts">
import { ICONS } from "@/assets/icons";
import ProductCardGrid from "@/components/Shop/ProductCard/ProductCardGrid.vue";
import { SectionFiltersDialogProps } from "@/components/Shop/ProductSection/SectionFilters.dialog.vue";
import TagsChips, { TagsChipsApi } from '@/components/Ui/TagsChips.vue';
import TagsTabs from '@/components/Ui/TagsTabs.vue';
import { defaultEntityOptionsProps } from "@/core/entity/constants";
import { useDebugStore } from "@/core/store/debug";
import { ProductSection } from "@/gql/gen";
import { ProductModel } from "@/modules/shop/composable/useProduct";
import { useCatalogStore } from "@/modules/shop/store/catalog";
import { useLayoutStore } from "@/stores/layout";
import { useDebounceFn, useElementVisibility, watchIgnorable } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { QNoSsr, useQuasar } from "quasar";
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from "vue";
import SectionFiltersDialog from "./SectionFilters.dialog.vue";
import ViewModeMenu from './ViewModeMenu.vue';

const $q = useQuasar()

type ProductSectionProps = {
  section: ProductSection
}

const props = withDefaults(defineProps<ProductSectionProps>(), {})

const {section} = toRefs(props)

// Отслеживание видимости секции
const sectionRef = useTemplateRef<HTMLDivElement>('sectionRef')
const isVisible = useElementVisibility(sectionRef)

const refTagsSecondary = ref<TagsChipsApi | null>(null)

const layoutStore = useLayoutStore()

const {headerHeightCss, headerHeight} = storeToRefs(layoutStore)

// Оптимизированные значения headerHeight - обновляются только когда секция видима
const cachedHeaderHeightCss = ref(headerHeightCss.value)
const cachedHeaderHeight = ref(headerHeight.value)

// Обновляем кешированные значения только когда секция видима
watch([headerHeightCss, isVisible], ([newHeightCss, visible]) => {
  if (visible) {
    cachedHeaderHeightCss.value = newHeightCss
  }
})

watch([headerHeight, isVisible], ([newHeight, visible]) => {
  if (visible) {
    cachedHeaderHeight.value = newHeight
  }
})

const debugStore = useDebugStore()
const {debugOptions} = storeToRefs(debugStore)

const catalogStore = useCatalogStore()

const {productsModelsBySection, productIngredientsById, productFilterIngredientsById, viewMode} = storeToRefs(catalogStore)

// Объявляем переменные фильтров ДО их использования в computed
const selectedTagPrimary = ref<number | null>(0)
const selectedTagsSecondary = ref<number[]>([])

// Отложенные значения для фильтрации (обновляются через setTimeout)
const selectedTagPrimaryForFilter = ref<number | null>(0)
const selectedTagsSecondaryForFilter = ref<number[]>([])

// Флаг загрузки для визуальной обратной связи
const isFiltering = ref(false)

const products = computed<ProductModel[]>(() => {
  if (!section.value) return []
  return productsModelsBySection.value[section.value.ID] || []
})

const productsFiltered = computed<ProductModel[]>(() => {
  // Используем отложенные значения для фильтрации
  let filtered = products.value.filter(product => {
    if (selectedTagPrimaryForFilter.value) {
      if (!product.IBLOCK_SECTION_IDS.includes(selectedTagPrimaryForFilter.value)) {
        return false
      }
    }
    if (selectedTagsSecondaryForFilter.value.length) {
      if (!product.INGREDIENTS.find(item => selectedTagsSecondaryForFilter.value.includes(item.ELEMENT_ID))) {
        return false
      }
    }
    return true
  })

  // Применяем сортировку
  const sorted = [...filtered].sort((a, b) => {
    switch (sortOption.value) {
      case 'price_asc':
        // Сначала дешевле
        return (a.PRICE?.PRICE || 0) - (b.PRICE?.PRICE || 0)

      case 'price_desc':
        // Сначала дороже
        return (b.PRICE?.PRICE || 0) - (a.PRICE?.PRICE || 0)

      case 'popular':
        // По популярности (по количеству продаж)
        return (b.SALES_COUNT || 0) - (a.SALES_COUNT || 0)

      case 'name_asc':
        // По имени (А-Я)
        return (a.NAME || '').localeCompare(b.NAME || '', 'ru')

      default:
        return 0
    }
  })

  // Ограничиваем количество товаров если включена опция
  if (debugOptions.value.limitProductsPerSection) {
    return sorted.slice(0, 6)
  }

  return sorted
})

const emit = defineEmits<{
  filtered: [ProductSection]
}>()


const tagsPrimary = computed(() => {
  if (!section.value || !section.value.CHILDREN) return []
  return [
    ...section.value.CHILDREN.map((item, index) => ({
      ID: item.ID,
      NAME: item.NAME || '',
    }))
  ]
})

const tagsSecondary = computed(() => {
  return Object.values(products.value.reduce<Record<string, number>>((map, product) => {
    product.INGREDIENTS.forEach((item) => map[item.ELEMENT_ID] = item.ELEMENT_ID)
    return map
  }, {})).map(id => productFilterIngredientsById.value[id]).filter(Boolean).map((item, index) => ({
    ID: item!.ID,
    NAME: item!.NAME || '',
    //FEATURED: index === 0
  }))
})

const hasFilters = computed(() => {
  return tagsPrimary.value.length > 0 || tagsSecondary.value.length > 0
})

// Флаг: был ли хотя бы раз применен фильтр
const wasFilterUsed = ref(false)

// Отслеживаем первое использование фильтра
watch([selectedTagPrimary, selectedTagsSecondary], ([newPrimary, newSecondary]) => {
  // Если выбран не первый таб или есть вторичные фильтры - фильтр использован
  if ((newPrimary !== null && newPrimary !== 0) || (newSecondary && newSecondary.length > 0)) {
    wasFilterUsed.value = true
  }
}, {deep: true})

// Определяем, нужно ли использовать TransitionGroup
// Используем только если:
// 1. Фильтр был использован хотя бы раз (wasFilterUsed)
// 2. Есть фильтры (hasFilters)
// 3. Не включен режим оптимальной производительности
const shouldUseTransition = computed(() => {
  if (debugOptions.value.optimalPerformance || !hasFilters.value || $q.screen.lt.md) return false
  return wasFilterUsed.value
})

// Дебаунс для фильтрации (только если есть фильтры)
const debouncedEmitFiltered = useDebounceFn(() => {
  emit('filtered', section.value)
}, 100)

const watchSelectedTagPrimary = watchIgnorable(selectedTagPrimary, async () => {
  if (selectedTagPrimary.value) {
    watchSelectedTagsSecondary.ignoreUpdates(() => {
      selectedTagsSecondary.value = []
      selectedTagsSecondaryForFilter.value = []
    })
  }

  // Устанавливаем флаг загрузки
  if ($q.screen.lt.md)
    isFiltering.value = true

  // Откладываем обновление фильтра на 150ms для медленных устройств
  // Это дает время анимации индикатора в TagsTabs полностью завершиться
  // перед началом тяжелого ререндера товаров
  setTimeout(() => {
    selectedTagPrimaryForFilter.value = selectedTagPrimary.value

    // Сбрасываем флаг загрузки после применения фильтра
    // Используем еще один setTimeout чтобы дать Vue время на ререндер

    if ($q.screen.lt.md) {
      setTimeout(() => {
        isFiltering.value = false
      }, 0)
    }

    if (hasFilters.value) {
      debouncedEmitFiltered()
    } else {
      emit('filtered', section.value)
    }
  }, 200)
})

const watchSelectedTagsSecondary = watchIgnorable(selectedTagsSecondary, async () => {
  if (selectedTagsSecondary.value.length) {
    watchSelectedTagPrimary.ignoreUpdates(() => {
      selectedTagPrimary.value = null
      selectedTagPrimaryForFilter.value = null
    })
  }

  // Устанавливаем флаг загрузки
  if ($q.screen.lt.md)
    isFiltering.value = true

  // Откладываем обновление фильтра на 150ms
  setTimeout(() => {
    selectedTagsSecondaryForFilter.value = [...selectedTagsSecondary.value]

    if ($q.screen.lt.md) {
      // Сбрасываем флаг загрузки
      setTimeout(() => {
        isFiltering.value = false
      }, 0)
    }

    if (hasFilters.value) {
      debouncedEmitFiltered()
    } else {
      emit('filtered', section.value)
    }
  }, 200)
}, {deep: true})

type ViewVariant = {
  default?: boolean
  sectionCodes?: string[]
  sectionIds?: number[]
  cellClass: {
    grid: string,
    list: string
  },
  cellComponent: any
}

type ViewVariantCurrent = Omit<ViewVariant, 'cellClass'> & {
  cellClass: string,
}

const viewVariants: ViewVariant[] = [
  {
    default: true,
    cellClass: {
      grid: 'col-12 col-lg-8 col-xl-6',
      list: 'col-24 col-md-8 col-xl-6',
    },
    cellComponent: ProductCardGrid
  },
  /*
    {
      sectionCodes: ['sets'],
      cellClass: 'col-12 col-lg-12',
      cellComponent: ProductCardGridBig
    }
   */
]

const viewVariant = computed(() => {
  let result = {} as ViewVariantCurrent
  viewVariants.forEach((variant) => {
    if (variant.sectionCodes && section.value.CODE && !variant.sectionCodes.includes(section.value.CODE)) {
      return
    }
    if (variant.sectionIds && !variant.sectionIds.includes(section.value.ID)) {
      return
    }
    result = {
      ...result,
      ...variant,
      cellClass: variant.cellClass[viewMode.value]
    }
    return result
  })
  return result
})

const stickable = computed(() => {
  if (!section.value) return false
  return !!tagsPrimary.value.length || true
})

const filtersDialog = ref<boolean>(false)

const onFiltersOpen = () => {
  filtersDialog.value = true
}

const onFiltersDialogApply: SectionFiltersDialogProps['apply'] = async (data) => {
  selectedTagPrimary.value = data.valuePrimaryTag
  selectedTagsSecondary.value = data.valueSecondaryTag

  await nextTick()
  if (selectedTagsSecondary.value.length) {
    refTagsSecondary.value?.scrollToSelected()
  } else {
    refTagsSecondary.value?.scrollToStart()
  }
}

watch(viewVariant, () => {

}, {deep: true})

// Debug композабл для отслеживания ререндеров (закомментировано для production)
// useDebugComponent('ProductSection', { collectRenders: true });

const onChangeViewMode = () => {
  // Определяем новый режим
  const newMode = viewMode.value === 'grid' ? 'list' : 'grid';

  // Используем метод store, который сам управляет событием и задержкой
  catalogStore.setViewMode(newMode);
};

const onViewModeChange = (newMode: 'grid' | 'list') => {
  catalogStore.setViewMode(newMode);
};

const onSortChange = (newSort: string) => {
  sortOption.value = newSort;
};

// Сортировка
const sortOption = ref('price_asc');
const sortOptions = [
  { label: 'Сначала дешевле', value: 'price_asc' },
  { label: 'Сначала дороже', value: 'price_desc' },
  { label: 'По популярности', value: 'popular' },
  { label: 'По имени', value: 'name_asc' },
];

const viewOrientation = (product: ProductModel) => {
  return section.value.CODE === 'sets' ? 'hor' : 'vert'
}

</script>

<style lang="scss" scoped>

.c-header-desktop {
  background-color: #FFFFFF;
  border-radius: 10px;
  border: 1px solid #eee;
  z-index: 10;
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 12px 16px;

  .c-tags-primary {
    margin-top: 4px;
  }

  &.--stickable {
    position: sticky;
  }

  &.stacked {
    box-shadow: 0 5px 15px -5px rgba(34, 60, 80, 0.2);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 7px 16px;
  }
}

.c-header-mobile {
  border-top: 1px solid #DDDDDD;
  padding-top: 8px;

  &:not(.--with-tags) {
    padding-bottom: 8px;
    border-bottom: 1px solid #DDDDDD;
  }
}

.c-tags-primary-mobile {
  position: sticky;
  z-index: 1;
  will-change: top;
  // transition: top 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-bottom: 1px solid #DDDDDD;

  .c-tags-primary {
    transition: all 0.15s ease-in-out;
  }

  .c-filter-button {
    position: relative;
    z-index: 10;
  }

  :deep(.q-tab__label) {
    font-size: 14px;
  }

  &.sticky {
    box-shadow: 0 4px 8px -3px rgba(34, 60, 80, 0.2);
    padding-top: 0;
    padding-bottom: 0;

    .c-tags-primary {
      padding-top: 0;
    }
  }
}

.c-tags-secondary-mobile {
  z-index: 1;
}

.c-products {
  position: relative;
}

.c-products__list {
  position: relative;
  transition: opacity 0.15s ease-in-out;

  &.--filtering {
    opacity: 0.6;
    pointer-events: none;
  }
}

.c-products__loading {
  position: absolute;
  top: 0px;
  left: 0;
  right: 0;
  z-index: 10;
}

</style>
